/* ==========================================================================
   LUXE Cart Drawer v2 - interaction layer
   --------------------------------------------------------------------------
   Progressive enhancement over the theme's cart AJAX/state layer. Nothing here
   forks cart state: adds/removes go through the theme's own endpoints and the
   drawer re-renders via <mini-cart>.renderContents / the cartUpdate pub-sub.

   Responsibilities:
   1. Re-hydrate the delivery-estimate node (data-luxe-delivery) after each
      drawer render - reuses the luxe-stock-status.js engine (window.LuxeDelivery),
      so the estimate is always computed live, never a cached/rotting date.
   2e. [#08] Free gift auto add / remove on every render, driven by the single
      data-gift-action Liquid computes (same cart calls + re-render path as the
      protection re-tier; test mode stamps luxe_gift_test first).
   2. Shipping Protection TWO-BUTTON CHOICE at the foot (replaced the toggle
      2026-07-20): "Checkout with protection · £X" adds the correct-tier variant
      then submits; "Checkout without protection" is a native submit that we only
      intercept to strip a stale protection line. A failed add never falls through
      to checkout. Locked while a request is in flight (qty always 1). Plus three
      background behaviours run from afterRender() on every re-render:
        2b. auto-clean a protection-only cart (data-sp-orphan -> /cart/clear.js);
        2c. assign + stamp the benefit-copy A/B bucket on first drawer open
            (sp_copy via /cart/update.js) - both arms see the choice, only the
            wording differs;
        2d. debounced auto re-tier when the order value crosses a band, with a
            quiet notice line that survives section re-renders.
   3. Swipe-down to close on mobile (header drag) - dispatches the same
      drawer:force-close event the close button uses (theme focus-return intact).
   Vanilla JS, deferred, no dependencies. A MutationObserver on #mini-cart is the
   single "re-rendered" signal that drives afterRender for both our mutations and
   the theme's own cart AJAX.
   ========================================================================== */
(function () {
  'use strict';

  function drawerBody() { return document.getElementById('mini-cart'); }
  function cartDrawerEl() { return document.querySelector('cart-drawer'); }

  /* ---- 1. delivery estimate re-hydration ------------------------------- */
  /* Once per RENDER: computeWithin rewrites the estimate text on every call,
     which mutates #mini-cart, which re-fires the render observer, which calls
     afterRender again - an infinite ~60ms loop that permanently reset the
     re-tier debounce (found 2026-07-13: afterRender ran 64x in 5s, re-tier
     never fired). The marker attribute dies with each genuine re-render (fresh
     DOM), so every render still hydrates exactly once. */
  function hydrateDelivery() {
    var root = drawerBody();
    if (!root) return;
    var body = root.querySelector('.luxe-cd');
    if (!body || body.getAttribute('data-luxe-delivery-hydrated') === 'true') return;
    if (window.LuxeDelivery && typeof window.LuxeDelivery.computeWithin === 'function') {
      body.setAttribute('data-luxe-delivery-hydrated', 'true');
      window.LuxeDelivery.computeWithin(root);
    }
  }

  /* ---- shared re-render helpers (reuse the theme's flow) ---------------- */
  function sectionIds() {
    var mc = document.querySelector('mini-cart');
    if (mc && typeof mc.getSectionsToRender === 'function') {
      return mc.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  function renderFromState(state) {
    var mc = document.querySelector('mini-cart');
    if (mc && typeof mc.renderContents === 'function') {
      mc.renderContents(state);
    }
    afterRender();
  }

  /* Cart AJAX routes, resolved from the theme so locale prefixes are respected. */
  function routeAdd() { return theme.routes.cart_add_url; }
  function routeChange() { return theme.routes.cart_change_url; }
  function routeUpdate() { return theme.routes.cart_update_url; }
  /* theme.routes urls are extension-less (/cart/change) - replace the PATH
     segment, keeping any locale prefix. The old 'change.js'->'clear.js' replace
     never matched, so auto-clean silently POSTed to /cart/change (no-op). */
  function routeClear() { return theme.routes.cart_change_url.replace('/cart/change', '/cart/clear'); }

  /* ---- 2. Shipping Protection: state + shared plumbing ------------------ */
  var spBusy = false;          /* a cart mutation we own is in flight - block re-entrancy */
  var spNotice = null;         /* { text, expiry } - re-applied across section re-renders */
  var retierTimer = null;      /* debounce handle for the auto re-tier */
  var noticeHideTimer = null;

  function spRoot() {
    var body = drawerBody();
    return body ? body.querySelector('.luxe-cd') : null;
  }
  function spRow() {
    var body = drawerBody();
    return body ? body.querySelector('[data-sp-row]') : null;
  }

  /* ---- 2a. the two-button choice ---------------------------------------- */
  /* Replaced the toggle on 2026-07-20. Each button is a single decision that both
     records the protection choice AND proceeds to checkout, so the buyer never has
     to make the same call twice. Neither is pre-selected: express consent comes
     from the tap (Consumer Contracts Regs 2013 reg. 40).

     BOTH buttons are native submits in the markup, so the money CTA never depends
     on this file loading (Dan 07-20). What follows is an UPGRADE, not the mechanism:

     Accept  : natively posts /cart/add with return_to=/checkout. With JS we
               intercept, add over AJAX and submit - so a failed add can surface an
               error in the drawer instead of bouncing to a Shopify error page.
               Sending someone to checkout believing they bought protection they do
               not have would be a real consumer harm, so a failed add must never
               silently fall through to a plain checkout.
     Decline : natively submits the cart form. We only intercept when a protection
               line is already in the cart (buyer added it at checkout then came
               back, or tapped accept and returned) - then remove it, then submit. */

  function choiceRow() { return spRow(); }

  function setChoiceBusy(row, busy) {
    if (!row) return;
    row.classList.toggle('is-busy', !!busy);
    var btns = row.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) { btns[i].disabled = !!busy; }
    if (busy) { row.setAttribute('aria-busy', 'true'); } else { row.removeAttribute('aria-busy'); }
  }

  function showChoiceError(show) {
    var body = drawerBody();
    var node = body && body.querySelector('[data-sp-error]');
    if (!node) return;
    if (show) { node.removeAttribute('hidden'); } else { node.setAttribute('hidden', ''); }
  }

  /* Submit the theme's cart form the way the native button would. form.submit()
     skips the submitter, so the "checkout" name never reaches Shopify - add it as
     a hidden field instead, otherwise the POST just re-renders /cart. */
  function submitCheckout() {
    /* Release the lock BEFORE navigating. Normally the page leaves and none of
       this matters, but if navigation is blocked (bfcache restore, a beforeunload
       handler, an extension, the gate's harness) a stuck spBusy would silently
       disable auto-clean, bucket stamping, re-tier AND the decline path for the
       rest of the session. Fail open, not wedged. */
    spBusy = false;
    setChoiceBusy(choiceRow(), false);
    var form = document.getElementById('cart');
    if (!form) { window.location.href = '/checkout'; return; }
    if (!form.querySelector('input[name="checkout"]')) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  function spIsInCart(row) {
    return !!row && row.getAttribute('data-sp-in-cart') === 'true';
  }

  function onCheckoutWith(event) {
    var btn = event.target.closest ? event.target.closest('[data-sp-checkout-with]') : null;
    if (!btn) return;
    event.preventDefault();
    if (spBusy) return;

    var row = choiceRow();
    /* Already protected (re-tier has kept it on the right band) - just go. */
    if (spIsInCart(row)) { submitCheckout(); return; }

    var variantId = row && row.getAttribute('data-sp-target-id');
    if (!variantId) { submitCheckout(); return; }

    showChoiceError(false);
    spBusy = true;
    setChoiceBusy(row, true);

    fetch(routeAdd(), Object.assign({}, fetchConfig('javascript'), {
      body: JSON.stringify({ id: variantId, quantity: 1 })
    }))
      .then(function (r) { return r.json(); })
      .then(function (state) {
        /* 422 = variant unavailable/sold out. Do NOT proceed silently. */
        if (state && state.status && state.status !== 200) {
          spBusy = false; setChoiceBusy(row, false); showChoiceError(true);
          return;
        }
        submitCheckout();
      })
      .catch(function () {
        spBusy = false; setChoiceBusy(row, false); showChoiceError(true);
      });
  }

  function onCheckoutWithout(event) {
    var btn = event.target.closest ? event.target.closest('[data-sp-checkout-without]') : null;
    if (!btn) return;

    var row = choiceRow();
    /* Nothing to strip - let the native submit run untouched (this is also the
       no-JS path, which is why the decline stays a real submit button). */
    if (!spIsInCart(row)) return;

    event.preventDefault();
    if (spBusy) return;

    var key = row.getAttribute('data-sp-line-key');
    var idx = row.getAttribute('data-sp-line-index');
    if (!key && !idx) { submitCheckout(); return; }

    spBusy = true;
    setChoiceBusy(row, true);

    var payload = key ? { id: key } : { line: parseInt(idx, 10) };
    payload.quantity = 0;
    fetch(routeChange(), Object.assign({}, fetchConfig(), { body: JSON.stringify(payload) }))
      .then(function (r) { return r.json(); })
      .then(function () { submitCheckout(); })
      .catch(function () {
        /* Could not strip it: stay put rather than charge for a declined add-on. */
        spBusy = false; setChoiceBusy(row, false); showChoiceError(true);
      });
  }

  /* ---- 2b. auto-clean a protection-only cart --------------------------- */
  /* A protection fee alone is never a valid basket (buyer removed every product
     but left the add-on). Liquid flags it with data-sp-orphan; we clear the cart
     so the drawer falls back to its genuine empty state. */
  function maybeAutoClean() {
    if (spBusy) return false;
    var root = spRoot();
    if (!root || root.getAttribute('data-sp-orphan') !== 'true') return false;
    spBusy = true;
    fetch(routeClear(), Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ sections: sectionIds(), sections_url: window.location.pathname })
    }))
      .then(function (r) { return r.json(); })
      .then(function (state) { spBusy = false; renderFromState(state); })
      .catch(function () { spBusy = false; });
    return true;
  }

  /* ---- 2c. A/B bucket assignment (benefit-copy test) ------------------- */
  /* On first drawer open, if the cart has no sp_copy attribute, assign a 50/50
     bucket and stamp it via /cart/update.js. It persists to the order as a note
     attribute, which is how attach rate is read per arm.
     BOTH arms see the same two-button choice - only the benefit wording differs.
     Key is `sp_copy`, NOT the retired `sp_ab` (test 1, closed 2026-07-20), so old
     buckets can never bleed into this read. */
  function maybeAssignCopyBucket() {
    if (spBusy || !drawerOpen) return false;
    var root = spRoot();
    if (!root) return false;
    if (root.getAttribute('data-sp-configured') !== 'true') return false;
    if (root.getAttribute('data-sp-copy-test') !== 'true') return false;
    var current = root.getAttribute('data-sp-copy');
    if (current === 'A' || current === 'B') return false;
    var bucket = Math.random() < 0.5 ? 'A' : 'B';
    spBusy = true;
    fetch(routeUpdate(), Object.assign({}, fetchConfig(), {
      body: JSON.stringify({
        attributes: { sp_copy: bucket },
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    }))
      .then(function (r) { return r.json(); })
      .then(function (state) { spBusy = false; renderFromState(state); })
      .catch(function () { spBusy = false; });
    return true;
  }

  /* ---- 2d. auto re-tier on band crossing (debounced) ------------------- */
  /* If an SP line is present but on the wrong tier for the current order value,
     Liquid marks the row data-sp-drift. Swap to the correct-band variant in ONE
     atomic /cart/update.js (old -> 0, new -> 1) and surface the quiet notice.
     Atomic matters: the earlier remove-then-add pair left a no-protection
     mid-state, and its 2-request window let a concurrent stepper press trip the
     theme's item_count heuristic ("You can only add N..." flash - Dan hit it
     2026-07-12). Checkout never re-tiers; it inherits the corrected variant. */
  function maybeRetier() {
    if (spBusy) return false;
    var row = spRow();
    if (!row || row.getAttribute('data-sp-drift') !== 'true') {
      /* No drift on THIS render: cancel any pending swap. Without this, a timer
         armed before the cart changed fires anyway - and update.js CREATES a
         line for an absent variant id, i.e. it would resurrect protection the
         buyer just removed. */
      clearTimeout(retierTimer);
      return false;
    }

    var oldVid = row.getAttribute('data-sp-cart-vid');
    var targetId = row.getAttribute('data-sp-target-id');
    var targetPrice = row.getAttribute('data-sp-target-price') || '';
    var tpl = row.getAttribute('data-sp-notice-tpl') || 'Protection updated to {price} as your order total changed.';
    if (!targetId || !oldVid || oldVid === targetId) return false;

    clearTimeout(retierTimer);
    retierTimer = setTimeout(function () {
      if (spBusy) return;
      /* Re-validate against the LIVE render - the world may have changed during
         the debounce (toggle off, items removed, tier already corrected). */
      var liveRow = spRow();
      if (!liveRow || liveRow.getAttribute('data-sp-drift') !== 'true') return;
      if (liveRow.getAttribute('data-sp-cart-vid') !== oldVid) return;
      if (liveRow.getAttribute('data-sp-target-id') !== targetId) return;
      spBusy = true;
      var updates = {};
      updates[oldVid] = 0;
      updates[targetId] = 1;
      fetch(routeUpdate(), Object.assign({}, fetchConfig(), {
        body: JSON.stringify({
          updates: updates,
          sections: sectionIds(), sections_url: window.location.pathname
        })
      }))
        .then(function (r) { return r.json(); })
        .then(function (state) {
          spNotice = { text: tpl.replace('{price}', targetPrice), expiry: Date.now() + 7000 };
          spBusy = false;
          renderFromState(state);
        })
        .catch(function () { spBusy = false; });
    }, 600);
    return true;
  }

  /* ---- 2e. [#08] free gift: auto add / remove (debounced) ----------------- */
  /* ONE gift (the LED Face Mask), free at a spend threshold. Liquid does all the
     maths on every render (qualifying subtotal, threshold in the cart currency,
     whether the gift line is present) and tells us the single action for this
     render on the drawer root: data-gift-action = add | remove | fix-qty | none.
     We only carry it out, the same way the protection re-tier works: debounced,
     re-validated against the LIVE render before firing, one owned mutation at a
     time (spBusy), and the response's sections re-render the drawer.
       add     = POST /cart/add    the gift variant, qty 1, property _luxe_gift=1
                 (the property is how Liquid tells the gift apart from a mask the
                 customer bought at full price, which is never touched)
       remove  = POST /cart/change the gift line key to qty 0
       fix-qty = POST /cart/change the gift line back to qty 1 (two tabs, old add)
     TEST MODE (data-gift-test="on", attribute name in data-gift-test-attr, both
     from the shop metafield luxe_gift.config): the checkout functions that make
     the gift free / block checkout under the threshold only fire for carts
     carrying that attribute = 1, so the test can run on NEXT without touching
     every live cart. In test mode we stamp the attribute first (POST /cart/update)
     and only auto-add once the render shows it stamped; test mode off = no attribute.
     The money side never depends on this file: the discount + validation
     functions price and police the gift in checkout whatever the drawer did. */
  var giftTimer = null;
  var giftFailUntil = 0;       /* an add that came back non-200 (sold out) is not retried for 60s */

  function giftAction(root) {
    if (!root || root.getAttribute('data-gift') !== 'on') return 'none';
    var action = root.getAttribute('data-gift-action') || 'none';
    var testOn = root.getAttribute('data-gift-test') === 'on';
    var stamped = root.getAttribute('data-gift-test-stamped') === 'true';
    if (testOn && !stamped) {
      /* nothing to stamp on an empty cart; and never add before the stamp */
      if (root.classList.contains('luxe-cd--empty')) return 'none';
      return 'stamp';
    }
    if (action === 'add' && Date.now() < giftFailUntil) return 'none';
    return action;
  }

  function maybeGiftSync() {
    var root = spRoot();
    var action = giftAction(root);
    if (action === 'none') {
      /* no action on THIS render: cancel a pending one (same trap as re-tier -
         a timer armed before the cart changed must never fire on stale state) */
      clearTimeout(giftTimer);
      return false;
    }
    if (spBusy) return false;
    clearTimeout(giftTimer);
    giftTimer = setTimeout(function () {
      if (spBusy) return;
      var live = spRoot();
      if (giftAction(live) !== action) return;
      var vid = live.getAttribute('data-gift-vid');
      var key = live.getAttribute('data-gift-line-key');
      /* the property and attribute NAMES come from the shop metafield config
         (via Liquid), so the drawer and the checkout functions read the same ones */
      var prop = live.getAttribute('data-gift-prop') || '_luxe_gift';
      var testAttr = live.getAttribute('data-gift-test-attr');
      var route, body, kv;
      if (action === 'stamp') {
        if (!testAttr) return;
        kv = {}; kv[testAttr] = '1';
        route = routeUpdate(); body = { attributes: kv };
      } else if (action === 'add') {
        if (!vid) return;
        kv = {}; kv[prop] = '1';
        route = routeAdd(); body = { id: vid, quantity: 1, properties: kv };
      } else if (action === 'remove') {
        if (!key) return;
        route = routeChange(); body = { id: key, quantity: 0 };
      } else if (action === 'fix-qty') {
        if (!key) return;
        route = routeChange(); body = { id: key, quantity: 1 };
      } else {
        return;
      }
      body.sections = sectionIds();
      body.sections_url = window.location.pathname;
      spBusy = true;
      fetch(route, Object.assign({}, fetchConfig(), { body: JSON.stringify(body) }))
        .then(function (r) { return r.json(); })
        .then(function (state) {
          spBusy = false;
          /* 422 = variant unavailable / sold out: leave the bar as it is, do not loop */
          if (state && state.status && state.status !== 200) {
            if (action === 'add') giftFailUntil = Date.now() + 60000;
            return;
          }
          renderFromState(state);
        })
        .catch(function () {
          spBusy = false;
          if (action === 'add') giftFailUntil = Date.now() + 60000;
        });
    }, 500);
    return true;
  }

  /* keep the notice text alive across section re-renders (idempotent: only writes
     when the value actually changes, so it never re-triggers the observer) */
  function hydrateSpNotice() {
    var body = drawerBody();
    if (!body) return;
    var node = body.querySelector('[data-sp-notice]');
    if (!node) return;
    if (spNotice && Date.now() < spNotice.expiry) {
      if (node.textContent !== spNotice.text) node.textContent = spNotice.text;
      if (node.hasAttribute('hidden')) node.removeAttribute('hidden');
      clearTimeout(noticeHideTimer);
      noticeHideTimer = setTimeout(function () {
        spNotice = null;
        var n = drawerBody() && drawerBody().querySelector('[data-sp-notice]');
        if (n) { n.textContent = ''; n.setAttribute('hidden', ''); }
      }, Math.max(0, spNotice.expiry - Date.now()));
    } else {
      spNotice = null;
      if (node.textContent !== '') node.textContent = '';
      if (!node.hasAttribute('hidden')) node.setAttribute('hidden', '');
    }
  }

  /* Single post-render hook. The MutationObserver on #mini-cart drives this after
     EVERY re-render (ours and the theme's). Order matters: clean an orphan cart
     first (it makes the rest moot), then the free gift (it changes the lines the
     rest reads), then stamp A/B, then re-tier. Each guards on spBusy so only one
     owned mutation is ever in flight. */
  function afterRender() {
    hydrateDelivery();
    hydrateSpNotice();
    if (maybeAutoClean()) return;
    if (maybeGiftSync()) return;
    if (maybeAssignCopyBucket()) return;
    maybeRetier();
  }

  /* ---- 3. swipe-down to close (mobile) ---------------------------------- */
  var startY = null, startX = null, tracking = false;

  function swipeStart(event) {
    if (window.matchMedia('(min-width: 750px)').matches) return; /* peek panel on desktop, no swipe */
    var t = event.touches ? event.touches[0] : event;
    /* Grab-handle visual removed; swipe-to-close now originates on the header only. */
    var onHandle = event.target.closest && event.target.closest('.luxe-cd__head');
    if (!onHandle) return;
    startY = t.clientY; startX = t.clientX; tracking = true;
  }

  function swipeMove(event) {
    if (!tracking) return;
    var t = event.touches ? event.touches[0] : event;
    var dy = t.clientY - startY;
    var dx = Math.abs(t.clientX - startX);
    if (dy > 60 && dy > dx) {
      tracking = false;
      closeDrawer();
    }
  }

  function swipeEnd() { tracking = false; startY = null; startX = null; }

  function closeDrawer() {
    var closeBtn = drawerBody() && drawerBody().querySelector('drawer-close-button');
    if (closeBtn) {
      closeBtn.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true, cancelable: true, composed: true }));
      return;
    }
    var cd = cartDrawerEl();
    if (cd && typeof cd.closeMenuDrawer === 'function') cd.closeMenuDrawer(new Event('swipe'));
  }

  /* ---- 4. focus management + Escape (a11y) ----------------------------- */
  /* The theme opens <cart-drawer> programmatically (openMenuDrawer with no
     summary), so the theme's trapFocus is never invoked for the basket. We
     reuse the SAME global trapFocus/removeTrapFocus and the existing close
     path above - no fork of cart state. */
  var lastTrigger = null;
  var drawerOpen = false;

  function panelEl() {
    var root = drawerBody();
    return root ? root.querySelector('.luxe-cd') : null;
  }

  function drawerIsVisible() {
    var b = document.body.classList;
    return b.contains('mini-cart--opening') || b.contains('mini-cart--open');
  }

  function focusPanel() {
    var panel = panelEl();
    if (!panel) return;
    if (panel.getAttribute('tabindex') === null) panel.setAttribute('tabindex', '-1');
    var focusTarget = panel.querySelector('.luxe-cd__close') || panel;
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(panel, focusTarget);
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  function refocusIfOutside() {
    if (!drawerOpen) return;
    var a = document.activeElement;
    if (!a || !a.closest || !a.closest('.luxe-cd')) focusPanel();
  }

  function onDrawerOpened() {
    var panel = panelEl();
    if (!panel) return;
    lastTrigger = document.activeElement;
    focusPanel();
    /* Now that the drawer is open, run the SP lifecycle: this is where the A/B
       bucket is assigned + stamped on first open (maybeAssignAB needs drawerOpen). */
    afterRender();
    /* The theme's ProductForm re-focuses the ATC button once its fetch
       settles, and renderContents can replace the panel DOM after we
       focus - re-assert after both windows have passed. */
    window.setTimeout(refocusIfOutside, 400);
    window.setTimeout(refocusIfOutside, 1000);
  }

  function onDrawerClosed() {
    var returnTo = (lastTrigger && typeof lastTrigger.focus === 'function' && document.contains(lastTrigger)) ? lastTrigger : null;
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(returnTo);
    } else if (returnTo) {
      returnTo.focus();
    }
    lastTrigger = null;
  }

  function watchOpenState() {
    var sync = function () {
      var visible = drawerIsVisible();
      if (visible === drawerOpen) return;
      drawerOpen = visible;
      if (visible) { onDrawerOpened(); } else { onDrawerClosed(); }
    };
    var mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function onKeydownEscape(event) {
    if (!drawerOpen) return;
    var key = event.key || event.code;
    if (key !== 'Escape' && key !== 'Esc') return;
    event.preventDefault();
    closeDrawer();
  }

  /* ---- wiring ---------------------------------------------------------- */
  /* MutationObserver on #mini-cart: the single source of "the drawer re-rendered".
     Fires afterRender for the theme's re-renders AND ours, so SP hydration /
     auto-clean / A/B / re-tier all re-apply. Debounced; afterRender is guarded
     + idempotent, so it never loops on its own writes. */
  function watchRenders() {
    var mc = drawerBody();
    if (!mc || !window.MutationObserver) return;
    var t = null;
    var mo = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(afterRender, 60);
    });
    mo.observe(mc, { childList: true, subtree: true });
  }

  function init() {
    /* Delegated: the drawer re-renders constantly, so the buttons are replaced DOM
       and can never hold their own listeners. Capture phase on the decline so we
       beat the form's native submit when a stale line has to be stripped first. */
    document.addEventListener('click', onCheckoutWith);
    document.addEventListener('click', onCheckoutWithout, true);

    /* re-hydrate + run SP lifecycle after every drawer render */
    watchRenders();
    document.addEventListener('cartdrawer:opened', afterRender);
    document.addEventListener('cart:updated', afterRender);
    document.addEventListener('ajaxProduct:added', afterRender);

    /* swipe-down close */
    var cd = cartDrawerEl();
    if (cd) {
      cd.addEventListener('touchstart', swipeStart, { passive: true });
      cd.addEventListener('touchmove', swipeMove, { passive: true });
      cd.addEventListener('touchend', swipeEnd, { passive: true });
    }

    /* focus trap on open, Escape to close, focus return on close */
    document.addEventListener('keydown', onKeydownEscape);
    watchOpenState();

    afterRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
